import { memo, useCallback, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { Heading } from '@library';
import HorizontalLayout from '../../../core/components/layout/HorizontalLayout';
import VerticalLayout from '../../../core/components/layout/VerticalLayout';
import ChevronLeftIcon from '../../../core/components/icons/ChevronLeftIcon';
import ChevronRightIcon from '../../../core/components/icons/ChevronRightIcon';
import Modal from '../../../core/components/Modal';
import PlayIcon from '../../../core/components/icons/PlayIcon';
import onDocumentKeydown from '../../../core/events/onDocumentKeydown';
import FlexLayout from '../../../core/components/layout/FlexLayout';
import ProductImage from '../ProductImage/ProductImage';
import ConsentRestriction from '../../../cookies/components/ConsentRestriction';
import type { MediaItem } from './ProductDetailsMedia';
import styles from './ProductDetailsGalleryModal.module.scss';

const ProductDetailsGalleryModal = ({
    title,
    onClose,
    media,
    activeItem,
    setActiveItem,
}: {
    title: string;
    onClose(): void;
    media: MediaItem[];
    activeItem: number;
    setActiveItem: React.Dispatch<React.SetStateAction<number>>;
}) => {
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const onPreviousItem = useCallback(() => {
        setActiveItem(currentActiveItem =>
            currentActiveItem > 0 ? currentActiveItem - 1 : media.length - 1
        );
    }, [media.length, setActiveItem]);

    const onNextItem = useCallback(() => {
        setActiveItem(currentActiveItem =>
            currentActiveItem < media.length - 1 ? currentActiveItem + 1 : 0
        );
    }, [media.length, setActiveItem]);

    const addKeyboardEventHandler =
        (callback: () => void) =>
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === ' ' || event.key === 'Enter') {
                callback();
            }
        };

    useEffect(() => {
        const arrowKeyNavigationHandler = (event: KeyboardEvent): void => {
            if (event.key === 'ArrowRight') {
                onNextItem();
            } else if (event.key === 'ArrowLeft') {
                onPreviousItem();
            }
        };

        const subscriptions: { unsubscribe: () => void }[] = [];

        subscriptions.push(onDocumentKeydown(arrowKeyNavigationHandler));

        return () => {
            subscriptions.forEach(sub => sub.unsubscribe());
        };
    }, [onNextItem, onPreviousItem]);

    const hasActiveItemIndex = activeItem >= 0 && activeItem < media.length;

    const activeMedia = media[activeItem];
    return (
        <Modal onClose={onClose} wide>
            <HorizontalLayout
                align="stretch"
                justify="spaceBetween"
                gap="large"
            >
                {hasActiveItemIndex && (
                    <div className={styles.mainImageContainer}>
                        {activeMedia.mediaType === 'IMAGE' && (
                            <ProductImage
                                renditionSize="large"
                                url={activeMedia.renditionUrl}
                                isSimilarImage={activeMedia.isSimilarImage}
                            />
                        )}
                        {activeMedia.mediaType === 'VIDEO' && (
                            <div className={styles.videoEmbedContainer}>
                                <ConsentRestriction
                                    className={styles.videoEmbed}
                                    consent={{
                                        functional: true,
                                    }}
                                >
                                    <iframe
                                        className={styles.videoEmbed}
                                        title="product video"
                                        src={`${activeMedia.url}?autoplay=1`}
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                    />
                                </ConsentRestriction>
                            </div>
                        )}
                    </div>
                )}
                <div className={styles.otherItems}>
                    <VerticalLayout justify="spaceBetween">
                        <div>
                            <div className={styles.title}>
                                <Heading h3>{title}</Heading>
                            </div>
                            <FlexLayout gap="small" standardColumns>
                                {media.map((mediaItem, index) =>
                                    mediaItem.url ? (
                                        <div
                                            role="button"
                                            tabIndex={-1}
                                            key={mediaItem.url}
                                            ref={ref => {
                                                itemRefs.current[index] = ref;
                                            }}
                                            className={clsx(styles.mediaItem, {
                                                [styles.mediaItemSelected]:
                                                    activeItem === index,
                                                [styles.videoItemThumbnail]:
                                                    mediaItem.mediaType ===
                                                    'VIDEO',
                                            })}
                                            onClick={() => setActiveItem(index)}
                                            onKeyPress={() => {}}
                                        >
                                            {mediaItem.mediaType ===
                                                'IMAGE' && (
                                                <ProductImage
                                                    renditionSize="mini"
                                                    url={mediaItem.renditionUrl}
                                                />
                                            )}
                                            {mediaItem.mediaType ===
                                                'VIDEO' && (
                                                <PlayIcon color="#fff" />
                                            )}
                                        </div>
                                    ) : (
                                        <div
                                            role="button"
                                            tabIndex={-1}
                                            key={mediaItem.url}
                                            className={clsx(styles.mediaItem, {
                                                [styles.mediaItemSelected]:
                                                    activeItem === index,
                                            })}
                                            onClick={() => setActiveItem(index)}
                                            onKeyPress={() => {}}
                                        >
                                            {mediaItem.component}
                                        </div>
                                    )
                                )}
                            </FlexLayout>
                        </div>
                        <HorizontalLayout gap="large" align="center">
                            <HorizontalLayout gap="medium" align="center">
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className={styles.controlItem}
                                    onClick={onPreviousItem}
                                    onKeyPress={addKeyboardEventHandler(
                                        onPreviousItem
                                    )}
                                >
                                    <ChevronLeftIcon color="#647fb1" />
                                </div>
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className={styles.controlItem}
                                    onClick={onNextItem}
                                    onKeyPress={addKeyboardEventHandler(
                                        onNextItem
                                    )}
                                >
                                    <ChevronRightIcon color="#647fb1" />
                                </div>
                            </HorizontalLayout>
                        </HorizontalLayout>
                    </VerticalLayout>
                </div>
            </HorizontalLayout>
        </Modal>
    );
};

export default memo(ProductDetailsGalleryModal);
